/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Schema, Node as ProsemirrorNode } from 'prosemirror-model';

const pDOM = ['p', 0];
const blockquoteDOM = ['blockquote', 0];
const preDOM = ['pre', ['code', 0]];
const brDOM = ['br'];
const olDOM = ['ol', 0];
const ulDOM = ['ul', 0];
const liDOM = ['li', 0];

// :: Object
// [Specs](#model.NodeSpec) for the nodes defined in this schema.
export const nodes = {
  // :: NodeSpec The top level document node.
  doc: {
    content: 'block+',
  },

  // :: NodeSpec A plain paragraph textblock. Represented in the DOM
  // as a `<p>` element.
  paragraph: {
    content: 'inline*',
    group: 'block',
    parseDOM: [{ tag: 'p' }],
    toDOM(node) {
      return pDOM;
    },
  },

  // :: NodeSpec A blockquote (`<blockquote>`) wrapping one or more blocks.
  blockquote: {
    content: 'block+',
    group: 'block',
    defining: true,
    parseDOM: [{ tag: 'blockquote' }],
    toDOM(node) {
      return blockquoteDOM;
    },
  },

  // :: NodeSpec A heading textblock, with a `level` attribute that
  // should hold the number 1 to 6. Parsed and serialized as `<h1>` to
  // `<h6>` elements.
  heading: {
    attrs: { level: { default: 1 } },
    content: 'inline*',
    group: 'block',
    defining: true,
    parseDOM: [
      { tag: 'h1', attrs: { level: 1 } },
      { tag: 'h2', attrs: { level: 2 } },
      { tag: 'h3', attrs: { level: 3 } },
      { tag: 'h4', attrs: { level: 4 } },
      { tag: 'h5', attrs: { level: 5 } },
      { tag: 'h6', attrs: { level: 6 } },
    ],
    toDOM(node) {
      return ['h' + node.attrs.level, 0];
    },
  },

  // :: NodeSpec A code listing. Disallows marks or non-text inline
  // nodes by default. Represented as a `<pre>` element with a
  // `<code>` element inside of it.
  code_block: {
    content: 'text*',
    marks: '',
    group: 'block',
    code: true,
    defining: true,
    parseDOM: [{ tag: 'pre', preserveWhitespace: 'full' }],
    toDOM(node) {
      return preDOM;
    },
  },

  // :: NodeSpec The text node.
  text: {
    group: 'inline',
  },

  // :: NodeSpec A hard line break, represented in the DOM as `<br>`.
  hard_break: {
    inline: true,
    group: 'inline',
    selectable: false,
    parseDOM: [{ tag: 'br' }],
    toDOM(node) {
      return brDOM;
    },
  },

  list_item: {
    parseDOM: [{ tag: 'li' }],
    toDOM() {
      return liDOM;
    },
    content: 'paragraph block*',
    defining: true,
  },

  orderedList: {
    attrs: { order: { default: 1 } },
    content: 'list_item+',
    parseDOM: [
      {
        tag: 'ol',
        getAttrs(dom) {
          return { order: dom.hasAttribute('start') ? +dom.getAttribute('start') : 1 };
        },
      },
    ],
    group: 'block',
    toDOM(node) {
      return node.attrs.order === 1 ? olDOM : ['ol', { start: node.attrs.order }, 0];
    },
  },

  // :: NodeSpec
  // A bullet list node spec, represented in the DOM as `<ul>`.
  bulletList: {
    content: 'list_item+',
    group: 'block',
    parseDOM: [{ tag: 'ul' }],
    toDOM() {
      return ulDOM;
    },
  }
};

const emDOM = ['em', 0];
const strongDOM = ['strong', 0];
const strikeDOM = ['strike', 0];
const underlineDOM = ['u', 0];
const superscriptDOM = ['sup', 0];

// :: Object [Specs](#model.MarkSpec) for the marks in the schema.
export const marks = {
  annotation: {
    attrs: {
      uuid: { default: null },
    },
    inclusive: false,
    parseDOM: [
      {
        tag: 'mark[uuid]',
        getAttrs(dom) {
          return {
            uuid: dom.getAttribute('uuid'),
          };
        },
      },
    ],
    toDOM(node) {
      const { uuid } = node.attrs;
      return ['mark', { uuid }, 0];
    },
    clearable: false,
  },

  // :: MarkSpec A link. Has `href` and `title` attributes. `title`
  // defaults to the empty string. Rendered and parsed as an `<a>`
  // element.
  link: {
    attrs: {
      href: {},
      title: { default: null },
      target: { default: '_self' },
    },
    inclusive: false,
    parseDOM: [
      {
        tag: 'a[href]',
        getAttrs(dom) {
          return {
            href: dom.getAttribute('href'),
            title: dom.getAttribute('title'),
            target: dom.getAttribute('target') || '_self',
          };
        },
      },
    ],
    toDOM(node) {
      const { href, title, target } = node.attrs;
      return ['a', { href, title, target }, 0];
    },
  },

  deleted: {
    toDOM() {
      return ['span', { class: 'removed' }];
    },
  },

  // :: MarkSpec An emphasis mark. Rendered as an `<em>` element.
  // Has parse rules that also match `<i>` and `font-style: italic`.
  em: {
    parseDOM: [{ tag: 'i' }, { tag: 'em' }, { style: 'font-style=italic' }],
    toDOM(node) {
      return emDOM;
    },
  },

  strike: {
    parseDOM: [{ tag: 'strike' }],
    toDOM(node) {
      return strikeDOM;
    },
  },

  underline: {
    parseDOM: [{ tag: 'u' }],
    toDOM(node) {
      return underlineDOM;
    },
  },

  // :: MarkSpec A strong mark. Rendered as `<strong>`, parse rules
  // also match `<b>` and `font-weight: bold`.
  strong: {
    parseDOM: [
      { tag: 'strong' },
      // This works around a Google Docs misbehavior where
      // pasted content will be inexplicably wrapped in `<b>`
      // tags with a font-weight normal.
      { tag: 'b', getAttrs: (node) => node.style.fontWeight !== 'normal' && null },
      {
        style: 'font-weight',
        getAttrs: (value) => /^(bold(er)?|[5-9]\d{2,})$/.test(value) && null,
      },
    ],
    toDOM(node) {
      return strongDOM;
    },
  },

  superscript: {
    parseDOM: [{ tag: 'sup' }],
    toDOM(node) {
      return superscriptDOM;
    },
  },
};

// :: Schema
// @ts-ignore
const schema = new Schema({ nodes, marks });
// @ts-ignore
schema.nullDoc = {
  state: {
    doc: { type: 'doc', content: [{ type: 'paragraph' }] },
    selection: { type: 'text', anchor: 1, head: 1 },
  },
  trackState: null,
};

export default schema;



// WEBPACK FOOTER //
// ./app/javascript/client/editor-core/miniSchema.js